// Auto-generated lightweight class for MBSE_Requirement
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { MBSE_RequirementReadAPI, MBSE_RequirementWriteAPI, MBSE_RequirementReadAPISchema, MBSE_RequirementWriteAPISchema } from '../types/mbse_requirement';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, HasMembers, MBSE_Attributes, MBSE_Derivation, MBSE_Refinement, MBSE_Requirements, MBSE_Satisfaction } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from '../../sysml/api/sysmlnamespace';
import { MBSE_Package } from './mbse_package';
import { MBSE_Action } from './mbse_action';
import { MBSE_Attribute } from './mbse_attribute';



export interface MBSE_Requirement extends Partial<MBSE_RequirementReadAPI> {}
export class MBSE_Requirement extends NeoNodeBase<MBSE_RequirementReadAPI, MBSE_RequirementWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "MBSE_Requirement";
  static readonly readSchema = MBSE_RequirementReadAPISchema;
  static readonly writeSchema = MBSE_RequirementWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    package: { target: "MBSE_Package", cardinality: "one", isParent: false, relClass: "MBSE_Requirements", direction: "in" },
    satisfyied_by: { target: "MBSE_Action", cardinality: "many", isParent: false, relClass: "MBSE_Satisfaction", direction: "out" },
    attributes: { target: "MBSE_Attribute", cardinality: "many", isParent: false, relClass: "MBSE_Attributes", direction: "out" },
    refined_by: { target: "MBSE_Requirement", cardinality: "many", isParent: false, relClass: "MBSE_Refinement", direction: "out" },
    refinement_of: { target: "MBSE_Requirement", cardinality: "one", isParent: false, relClass: "MBSE_Refinement", direction: "in" },
    derives_on: { target: "MBSE_Requirement", cardinality: "many", isParent: false, relClass: "MBSE_Derivation", direction: "out" },
    derived_from: { target: "MBSE_Requirement", cardinality: "one", isParent: false, relClass: "MBSE_Derivation", direction: "in" },
  };

  protected get modelName(): string {
    return 'MBSE_Requirement';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return MBSE_Requirement.label;
  }


  constructor(data: Partial<MBSE_RequirementWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: MBSE_RequirementReadAPI, loadContext?: Record<string, any>): MBSE_Requirement {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { security_scope, member_namespace, package: _package, satisfyied_by, attributes, refined_by, refinement_of, derives_on, derived_from, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = MBSE_RequirementReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, security_scope: security_scope, member_namespace: member_namespace, package: _package, satisfyied_by: satisfyied_by, attributes: attributes, refined_by: refined_by, refinement_of: refinement_of, derives_on: derives_on, derived_from: derived_from };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return MBSE_Requirement.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadOneRelationship(processed, 'package', 'MBSE_Package', ModelRegistry);
    loadManyRelationship(processed, 'satisfyied_by', 'MBSE_Action', ModelRegistry);
    loadManyRelationship(processed, 'attributes', 'MBSE_Attribute', ModelRegistry);
    loadManyRelationship(processed, 'refined_by', 'MBSE_Requirement', ModelRegistry);
    loadOneRelationship(processed, 'refinement_of', 'MBSE_Requirement', ModelRegistry);
    loadManyRelationship(processed, 'derives_on', 'MBSE_Requirement', ModelRegistry);
    loadOneRelationship(processed, 'derived_from', 'MBSE_Requirement', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new MBSE_Requirement(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'package' - Python ORM style (cached)
  get package(): RelationshipManager<this, MBSE_Package> {
    return this.getOrCreateRelationshipManager('package', () => {
      return new RelationshipManager(this, 'package', 'one', MBSE_Requirements as typeof Relationship);
    });
  }

  // Relationship manager for 'satisfyied_by' - Python ORM style (cached)
  get satisfyied_by(): RelationshipManager<this, MBSE_Action> {
    return this.getOrCreateRelationshipManager('satisfyied_by', () => {
      return new RelationshipManager(this, 'satisfyied_by', 'many', MBSE_Satisfaction as typeof Relationship);
    });
  }

  // Relationship manager for 'attributes' - Python ORM style (cached)
  get attributes(): RelationshipManager<this, MBSE_Attribute> {
    return this.getOrCreateRelationshipManager('attributes', () => {
      return new RelationshipManager(this, 'attributes', 'many', MBSE_Attributes as typeof Relationship);
    });
  }

  // Relationship manager for 'refined_by' - Python ORM style (cached)
  get refined_by(): RelationshipManager<this, MBSE_Requirement> {
    return this.getOrCreateRelationshipManager('refined_by', () => {
      return new RelationshipManager(this, 'refined_by', 'many', MBSE_Refinement as typeof Relationship);
    });
  }

  // Relationship manager for 'refinement_of' - Python ORM style (cached)
  get refinement_of(): RelationshipManager<this, MBSE_Requirement> {
    return this.getOrCreateRelationshipManager('refinement_of', () => {
      return new RelationshipManager(this, 'refinement_of', 'one', MBSE_Refinement as typeof Relationship);
    });
  }

  // Relationship manager for 'derives_on' - Python ORM style (cached)
  get derives_on(): RelationshipManager<this, MBSE_Requirement> {
    return this.getOrCreateRelationshipManager('derives_on', () => {
      return new RelationshipManager(this, 'derives_on', 'many', MBSE_Derivation as typeof Relationship);
    });
  }

  // Relationship manager for 'derived_from' - Python ORM style (cached)
  get derived_from(): RelationshipManager<this, MBSE_Requirement> {
    return this.getOrCreateRelationshipManager('derived_from', () => {
      return new RelationshipManager(this, 'derived_from', 'one', MBSE_Derivation as typeof Relationship);
    });
  }

}
